import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useMutation, useLazyQuery } from '@apollo/client'
import { ADD_RECIPIENT_PARTY, UPDATE_RECIPIENT_PARTY } from '../../../graphql/recipients/mutations'
import { GET_RECIPIENT_PARTY } from '../../../graphql/recipients/queries'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'


export default function RecipientPartyForm() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)
    const [recipientInfo, setRecipientInfo] = useState(null)


    const [getRecipientById, { loading: recipientLoading, error: recipientError }] = useLazyQuery(GET_RECIPIENT_PARTY, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.getRecipientParty.success && data.getRecipientParty.code === '200') {
                setRecipientInfo(data.getRecipientParty.recipients[0])
            }
        },
    })


    useEffect(() => {
        if (id) {
            getRecipientById({
                variables: {
                    searchFilter: {
                        recipient_id: id,
                        active: null,
                        search_string: null
                    },
                    page: {
                        limit: 1,
                        offset: 0,
                    },
                },
            })
        }
    }, [id])


    const handleBack = () => {
        navigate('/system-config/recipient-parties')
    }


    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }


    const [addRecipientParty, { loading: addLoading }] = useMutation(ADD_RECIPIENT_PARTY, {
        onCompleted: data => {
            console.log('Add recipient response:', data)


            if (data?.addRecipientParty?.code === '200' && data?.addRecipientParty?.success === true) {
                CreateRecipientFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                CreateRecipientFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setErrorMessage(data?.addRecipientParty?.message || 'An error occurred while creating the recipient')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            console.error('Add recipient error:', error)
            setSubmitLoading(false)
            CreateRecipientFormValidation.setSubmitting(false)
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })


    const [updateRecipientParty, { loading: updateLoading }] = useMutation(UPDATE_RECIPIENT_PARTY, {
        onCompleted: data => {
            console.log('Update recipient response:', data)


            if (data?.updateRecipientParty?.code === '200' && data?.updateRecipientParty?.success === true) {
                CreateRecipientFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                CreateRecipientFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setErrorMessage(data?.updateRecipientParty?.message || 'An error occurred while updating the recipient')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            console.error('Update recipient error:', error)
            setSubmitLoading(false)
            CreateRecipientFormValidation.setSubmitting(false)
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })


    const CreateRecipientFormValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: recipientInfo?.recipient_info?.name || '',
            email: recipientInfo?.recipient_info?.email || '',
            organization: recipientInfo?.recipient_info?.organization || '',
            country_code: recipientInfo?.country_code || '',
            active: recipientInfo?.active ?? true,
        },
        validationSchema: Yup.object().shape({
            name: Yup.string().required(t('Name is required')),
            email: Yup.string()
                .email(t('Invalid email'))
                .required(t('Email is required')),
            organization: Yup.string().required(t('Organization is required')),
            country_code: Yup.string().required(t('Country is required')),
        }),
        onSubmit: values => {
            setSubmitLoading(true)
            submitRecipient(values)
        },
    })


    const submitRecipient = values => {
        const recipientData = {
            recipient_info: {
                name: values.name,
                email: values.email,
                organization: values.organization,
            },
            country_code: values.country_code,
            active: values.active,
        }


        if (id) {
            updateRecipientParty({
                variables: {
                    recipient_id: id,
                    input: recipientData,
                },
            })
        } else {
            addRecipientParty({
                variables: {
                    input: recipientData,
                },
            })
        }
        console.log('Recipient data being sent:', { recipientId: id, recipientData })
    }


    const countryOptions = [
        { value: 'BH', label: 'Bahrain' },
        { value: 'AE', label: 'UAE' },
        { value: 'SA', label: 'Saudi Arabia' },
        { value: 'QA', label: 'Qatar' },
        { value: 'KW', label: 'Kuwait' },
        { value: 'OM', label: 'Oman' }
    ]


    return (
        <>
            <div className="mb-8 flex justify-between items-center">
                <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back')}
                </button>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={CreateRecipientFormValidation.isSubmitting}
                        onClick={CreateRecipientFormValidation.handleSubmit}
                        type="button"
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </div>


            {recipientLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
                    <p className="pt-3 text-primary font-semibold">Loading recipient data...</p>
                </div>
            )}


            {!recipientLoading && (
                <form>
                <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">{t('Recipient Information')}</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t('Enter the recipient details for email notifications')}
                            </p>
                        </div>


                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="name"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Name')} <span className="text-red ">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        value={CreateRecipientFormValidation.values.name}
                                        onChange={CreateRecipientFormValidation.handleChange}
                                        onBlur={CreateRecipientFormValidation.handleBlur}
                                        className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm/6 ${
                                            CreateRecipientFormValidation.touched.name && CreateRecipientFormValidation.errors.name
                                                ? 'ring-red-500 focus:ring-red-600'
                                                : 'ring-gray-300 focus:ring-indigo-600'
                                        }`}
                                    />
                                    {CreateRecipientFormValidation.touched.name && CreateRecipientFormValidation.errors.name && (
                                        <p className="mt-2 text-sm text-red ">{CreateRecipientFormValidation.errors.name}</p>
                                    )}
                                </div>
                            </div>


                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="email"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Email')} <span className="text-red ">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        type="email"
                                        name="email"
                                        id="email"
                                        value={CreateRecipientFormValidation.values.email}
                                        onChange={CreateRecipientFormValidation.handleChange}
                                        onBlur={CreateRecipientFormValidation.handleBlur}
                                        className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm/6 ${
                                            CreateRecipientFormValidation.touched.email && CreateRecipientFormValidation.errors.email
                                                ? 'ring-red-500 focus:ring-red-600'
                                                : 'ring-gray-300 focus:ring-indigo-600'
                                        }`}
                                    />
                                    {CreateRecipientFormValidation.touched.email && CreateRecipientFormValidation.errors.email && (
                                        <p className="mt-2 text-sm text-red ">{CreateRecipientFormValidation.errors.email}</p>
                                    )}
                                </div>
                            </div>


                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="organization"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Organization')} <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        type="text"
                                        name="organization"
                                        id="organization"
                                        value={CreateRecipientFormValidation.values.organization}
                                        onChange={CreateRecipientFormValidation.handleChange}
                                        onBlur={CreateRecipientFormValidation.handleBlur}
                                        className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm/6 ${
                                            CreateRecipientFormValidation.touched.organization && CreateRecipientFormValidation.errors.organization
                                                ? 'ring-red-500 focus:ring-red-600'
                                                : 'ring-gray-300 focus:ring-indigo-600'
                                        }`}
                                    />
                                    {CreateRecipientFormValidation.touched.organization && CreateRecipientFormValidation.errors.organization && (
                                        <p className="mt-2 text-sm text-red ">{CreateRecipientFormValidation.errors.organization}</p>
                                    )}
                                </div>
                            </div>


                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="country_code"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Country')} <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <select
                                        name="country_code"
                                        id="country_code"
                                        value={CreateRecipientFormValidation.values.country_code}
                                        onChange={CreateRecipientFormValidation.handleChange}
                                        onBlur={CreateRecipientFormValidation.handleBlur}
                                        className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm/6 ${
                                            CreateRecipientFormValidation.touched.country_code && CreateRecipientFormValidation.errors.country_code
                                                ? 'ring-red-500 focus:ring-red-600'
                                                : 'ring-gray-300 focus:ring-indigo-600'
                                        }`}
                                    >
                                        <option value="">{t('Select Country')}</option>
                                        {countryOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    {CreateRecipientFormValidation.touched.country_code && CreateRecipientFormValidation.errors.country_code && (
                                        <p className="mt-2 text-sm text-red ">{CreateRecipientFormValidation.errors.country_code}</p>
                                    )}
                                </div>
                            </div>


                            <div className="sm:col-span-6">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="active"
                                        id="active"
                                        checked={CreateRecipientFormValidation.values.active}
                                        onChange={(e) => CreateRecipientFormValidation.setFieldValue('active', e.target.checked)}
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                    />
                                    <label
                                        htmlFor="active"
                                        className="ml-3 text-sm font-medium leading-6 text-gray-900"
                                    >
                                        {t('Active')}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            )}


            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={t('Recipient party saved successfully')} onClose={handleDialogClose} />
            )}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
